import React, { useState } from "react";
import {
  Lock,
  Mail,
  User,
  MapPin,
  Globe,
  Hash,
  DollarSign,
  Users,
  ChevronRight,
  ChevronLeft,
  Image,
} from "lucide-react";
import { auth, database } from "../../../../Config/firebase";
import { createUserWithEmailAndPassword } from "../../../../Config/matuAuth";
import { ref, set } from "../../../../Config/matuRtdb";
import { useNavigate } from "react-router-dom";
import FootballLoading from "../../../../Utils/FootballLoading";

const generateReferralCode = () => {
  const characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let referralCode = "";
  for (let i = 0; i < 6; i++) {
    const randomIndex = Math.floor(Math.random() * characters.length);
    referralCode += characters[randomIndex];
  }
  return referralCode;
};

const RegisterAfiliatePage = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    gender: "",
    typeU: "",
    country: "",
    publicName: "",
    paymentMethod: "",
    biography: "",
    socialMedia: {
      instagram: "",
      twitter: "",
    },
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [agreeTerms, setAgreeTerms] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Manejo especial para campos anidados como socialMedia
    if (name.includes(".")) {
      const [parent, child] = name.split(".");
      setFormData({
        ...formData,
        [parent]: {
          ...formData[parent],
          [child]: value,
        },
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });

      // Actualizar typeU basado en el género
      if (name === "gender") {
        setFormData((prev) => ({
          ...prev,
          typeU: value === "masculino" ? "Afiliado" : "Afiliada",
        }));
      }
    }
  };

  const nextStep = () => {
    // Validaciones específicas por paso
    if (currentStep === 1) {
      if (!formData.name || !formData.email) {
        setError("Por favor completa todos los campos obligatorios");
        return;
      }
      // Validación básica de email
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        setError("Por favor ingresa un email válido");
        return;
      }
    }

    if (currentStep === 2) {
      if (!formData.password || !formData.confirmPassword) {
        setError("Por favor ingresa y confirma tu contraseña");
        return;
      }
      if (formData.password !== formData.confirmPassword) {
        setError("Las contraseñas no coinciden");
        return;
      }
      if (formData.password.length < 6) {
        setError("La contraseña debe tener al menos 6 caracteres");
        return;
      }
    }

    if (currentStep === 3) {
      if (!formData.gender || !formData.country) {
        setError("Por favor selecciona tu género y país");
        return;
      }
    }

    if (currentStep === 4) {
      if (!formData.publicName) {
        setError("Por favor ingresa un nombre público");
        return;
      }
    }

    setError("");
    setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    setError("");
    setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!agreeTerms) {
      setError("Debes aceptar los términos y condiciones");
      return;
    }

    if (!formData.paymentMethod) {
      setError("Por favor selecciona un método de pago");
      return;
    }

    setLoading(true);
    setError("");

    try {
      // Generar código de referido
      const referralCode = generateReferralCode();

      // Registrar al usuario
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );
      const user = userCredential.user;

      // Guardar el UID del usuario en el localStorage
      localStorage.setItem("userUUID", user.uid);

      // Guardar usuario y código de referido en la base de datos
      await set(ref(database, "usuarios/" + user.uid), {
        name: formData.name,
        email: formData.email,
        gender: formData.gender,
        typeU: formData.typeU,
        country: formData.country,
        publicName: formData.publicName,
        paymentMethod: formData.paymentMethod,
        biography: formData.biography || "",
        socialMedia: formData.socialMedia,
        referralCode: referralCode,
        createdAt: new Date().toISOString(),
        lastLogin: new Date().toISOString(),
      });

      // Redirigir al home después del registro exitoso
      navigate(`/dashboard/afiliate`);
    } catch (error) {
      console.error("Error al registrar usuario:", error);

      // Mensajes de error más descriptivos
      if (error.code === "auth/email-already-in-use") {
        setError("Este correo electrónico ya está registrado.");
      } else if (error.code === "auth/invalid-email") {
        setError("El formato del correo electrónico no es válido.");
      } else if (error.code === "auth/network-request-failed") {
        setError("Problema de conexión. Verifica tu internet.");
      } else {
        setError("Error al crear la cuenta. Por favor, intenta nuevamente.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-gray-900 to-black">
        <div className="mb-4">
          <img
            src="https://firebasestorage.googleapis.com/v0/b/towin-fe4cc.appspot.com/o/logoBlgo%2Fnuevo%20logo.png?alt=media&token=5bcd17e0-99cd-4e7c-a45b-d2928a449a40v0/b/towin-fe4cc.appspot.com/o/logoBlgo%2Ficon512x512preview.png?alt=media&token=00445cc5-773d-43aa-862e-6c437b48bce2"
            alt="Logo"
            className="w-16 h-16 rounded-full border-2 border-red-500"
          />
        </div>
        <FootballLoading />
        <p className="mt-4 text-white font-medium animate-pulse">
          Creando tu cuenta...
        </p>
      </div>
    );
  }

  // Lista de países latinoamericanos
  const countries = [
    "Argentina",
    "Bolivia",
    "Brasil",
    "Chile",
    "Colombia",
    "Costa Rica",
    "Cuba",
    "Ecuador",
    "El Salvador",
    "Guatemala",
    "Haití",
    "Honduras",
    "México",
    "Nicaragua",
    "Panamá",
    "Paraguay",
    "Perú",
    "República Dominicana",
    "Uruguay",
    "Venezuela",
  ];

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-gray-900 to-black py-8">
      {/* Tarjeta principal */}
      <div className="bg-gray-900 p-8 rounded-xl shadow-2xl max-w-md w-full border border-gray-800 relative overflow-hidden">
        {/* Elementos decorativos */}
        <div className="absolute -top-10 -right-10 w-40 h-40 bg-red-500 opacity-10 rounded-full"></div>
        <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-orange-500 opacity-10 rounded-full"></div>

        {/* Contenido */}
        <div className="relative z-10">
          {/* Logo y encabezado */}
          <div className="flex flex-col items-center mb-6">
            <img
              src="https://firebasestorage.googleapis.com/v0/b/towin-fe4cc.appspot.com/o/logoBlgo%2Fnuevo%20logo.png?alt=media&token=5bcd17e0-99cd-4e7c-a45b-d2928a449a40v0/b/towin-fe4cc.appspot.com/o/logoBlgo%2Ficon512x512preview.png?alt=media&token=00445cc5-773d-43aa-862e-6c437b48bce2"
              alt="Logo"
              className="w-20 h-20 rounded-full border-4 border-red-500 shadow-lg mb-3 transition-transform hover:scale-105 duration-300"
            />
            <h1 className="text-2xl font-bold text-white">DeportivosPro</h1>
            <div className="w-16 h-1 bg-gradient-to-r from-red-500 to-orange-500 rounded-full my-3"></div>
            <h2 className="text-xl font-medium text-gray-200">
              {formData.gender === "femenino"
                ? "Registro de Afiliada"
                : "Registro de Afiliado"}
            </h2>
          </div>

          {/* Indicador de pasos */}
          <div className="flex justify-center mb-6">
            <div className="flex items-center space-x-2">
              {[1, 2, 3, 4, 5].map((step) => (
                <div key={step} className="flex items-center">
                  <div
                    className={`w-6 h-6 rounded-full flex items-center justify-center text-xs
                      ${
                        currentStep === step
                          ? "bg-red-500 text-white"
                          : currentStep > step
                          ? "bg-green-500 text-white"
                          : "bg-gray-700 text-gray-300"
                      }`}
                  >
                    {currentStep > step ? "✓" : step}
                  </div>
                  {step < 5 && (
                    <div
                      className={`w-5 h-0.5 ${
                        currentStep > step ? "bg-green-500" : "bg-gray-700"
                      }`}
                    ></div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Mensaje de error */}
          {error && (
            <div className="mb-4 p-3 bg-red-900/30 border border-red-800 rounded-lg">
              <p className="text-red-400 text-sm flex items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
                {error}
              </p>
            </div>
          )}

          {/* Formulario en múltiples pasos */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Paso 1: Información básica */}
            {currentStep === 1 && (
              <>
                <h3 className="text-lg font-medium text-white mb-4">
                  Información Personal
                </h3>
                <div className="space-y-3">
                  <div className="space-y-1">
                    <label
                      htmlFor="name"
                      className="block text-gray-300 text-sm font-medium pl-1"
                    >
                      Nombre completo
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <User className="h-5 w-5 text-gray-500" />
                      </div>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full pl-10 pr-4 py-2 bg-gray-800/70 text-white border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200"
                        placeholder="Juan Pérez"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label
                      htmlFor="email"
                      className="block text-gray-300 text-sm font-medium pl-1"
                    >
                      Correo electrónico
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Mail className="h-5 w-5 text-gray-500" />
                      </div>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full pl-10 pr-4 py-2 bg-gray-800/70 text-white border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200"
                        placeholder="ejemplo@correo.com"
                        required
                      />
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Paso 2: Contraseñas */}
            {currentStep === 2 && (
              <>
                <h3 className="text-lg font-medium text-white mb-4">
                  Seguridad de tu Cuenta
                </h3>
                <div className="space-y-3">
                  <div className="space-y-1">
                    <label
                      htmlFor="password"
                      className="block text-gray-300 text-sm font-medium pl-1"
                    >
                      Contraseña
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Lock className="h-5 w-5 text-gray-500" />
                      </div>
                      <input
                        type="password"
                        id="password"
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        className="w-full pl-10 pr-4 py-2 bg-gray-800/70 text-white border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200"
                        placeholder="Mínimo 6 caracteres"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label
                      htmlFor="confirmPassword"
                      className="block text-gray-300 text-sm font-medium pl-1"
                    >
                      Confirmar contraseña
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Lock className="h-5 w-5 text-gray-500" />
                      </div>
                      <input
                        type="password"
                        id="confirmPassword"
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        className="w-full pl-10 pr-4 py-2 bg-gray-800/70 text-white border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200"
                        placeholder="Repite tu contraseña"
                        required
                      />
                    </div>
                  </div>
                </div>
                <div className="bg-gray-800/50 p-3 rounded-lg mt-3">
                  <p className="text-gray-400 text-sm">
                    <span className="text-yellow-500">Consejo:</span> Utiliza
                    una combinación de letras, números y símbolos para una
                    contraseña más segura.
                  </p>
                </div>
              </>
            )}

            {/* Paso 3: Género y país */}
            {currentStep === 3 && (
              <>
                <h3 className="text-lg font-medium text-white mb-4">
                  Información Demográfica
                </h3>
                <div className="space-y-3">
                  <div className="space-y-1">
                    <label
                      htmlFor="gender"
                      className="block text-gray-300 text-sm font-medium pl-1"
                    >
                      Género
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Users className="h-5 w-5 text-gray-500" />
                      </div>
                      <select
                        id="gender"
                        name="gender"
                        value={formData.gender}
                        onChange={handleChange}
                        className="w-full pl-10 pr-4 py-2 bg-gray-800/70 text-white border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200"
                        required
                      >
                        <option value="">Selecciona tu género</option>
                        <option value="masculino">Masculino</option>
                        <option value="femenino">Femenino</option>
                      </select>
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label
                      htmlFor="country"
                      className="block text-gray-300 text-sm font-medium pl-1"
                    >
                      País
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Globe className="h-5 w-5 text-gray-500" />
                      </div>
                      <select
                        id="country"
                        name="country"
                        value={formData.country}
                        onChange={handleChange}
                        className="w-full pl-10 pr-4 py-2 bg-gray-800/70 text-white border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200"
                        required
                      >
                        <option value="">Selecciona tu país</option>
                        {countries.map((country) => (
                          <option key={country} value={country}>
                            {country}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Paso 4: Perfil público */}
            {currentStep === 4 && (
              <>
                <h3 className="text-lg font-medium text-white mb-4">
                  Perfil Público
                </h3>
                <div className="space-y-3">
                  <div className="space-y-1">
                    <label
                      htmlFor="publicName"
                      className="block text-gray-300 text-sm font-medium pl-1"
                    >
                      Nombre público{" "}
                      {formData.gender === "femenino"
                        ? "de afiliada"
                        : "de afiliado"}
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <User className="h-5 w-5 text-gray-500" />
                      </div>
                      <input
                        type="text"
                        id="publicName"
                        name="publicName"
                        value={formData.publicName}
                        onChange={handleChange}
                        className="w-full pl-10 pr-4 py-2 bg-gray-800/70 text-white border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200"
                        placeholder="Tu nombre de afiliado público"
                        required
                      />
                    </div>
                    <p className="text-gray-400 text-xs mt-1 pl-1">
                      Este nombre será visible para otros usuarios
                    </p>
                  </div>

                  <div className="space-y-1">
                    <label
                      htmlFor="biography"
                      className="block text-gray-300 text-sm font-medium pl-1"
                    >
                      Biografía (opcional)
                    </label>
                    <textarea
                      id="biography"
                      name="biography"
                      value={formData.biography}
                      onChange={handleChange}
                      className="w-full pl-3 pr-3 py-2 bg-gray-800/70 text-white border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200"
                      placeholder="Cuéntanos un poco sobre ti..."
                      rows={3}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <label
                        htmlFor="instagram"
                        className="block text-gray-300 text-sm font-medium pl-1"
                      >
                        Instagram (opcional)
                      </label>
                      <input
                        type="text"
                        id="instagram"
                        name="socialMedia.instagram"
                        value={formData.socialMedia.instagram}
                        onChange={handleChange}
                        className="w-full pl-3 pr-3 py-2 bg-gray-800/70 text-white border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200"
                        placeholder="@usuario"
                      />
                    </div>
                    <div className="space-y-1">
                      <label
                        htmlFor="twitter"
                        className="block text-gray-300 text-sm font-medium pl-1"
                      >
                        Twitter (opcional)
                      </label>
                      <input
                        type="text"
                        id="twitter"
                        name="socialMedia.twitter"
                        value={formData.socialMedia.twitter}
                        onChange={handleChange}
                        className="w-full pl-3 pr-3 py-2 bg-gray-800/70 text-white border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200"
                        placeholder="@usuario"
                      />
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Paso 5: Método de pago y términos */}
            {currentStep === 5 && (
              <>
                <h3 className="text-lg font-medium text-white mb-4">
                  Finalizar Registro
                </h3>

                <div className="space-y-3">
                  <div className="space-y-1">
                    <label
                      htmlFor="paymentMethod"
                      className="block text-gray-300 text-sm font-medium pl-1"
                    >
                      Método de pago
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <DollarSign className="h-5 w-5 text-gray-500" />
                      </div>
                      <select
                        id="paymentMethod"
                        name="paymentMethod"
                        value={formData.paymentMethod}
                        onChange={handleChange}
                        className="w-full pl-10 pr-4 py-2 bg-gray-800/70 text-white border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200"
                        required
                      >
                        <option value="">Selecciona un método</option>
                        <option value="nequi">Nequi</option>
                        <option value="daviplata">Daviplata</option>
                        <option value="bancolombia">Bancolombia</option>
                        <option value="paypal">PayPal</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-gray-800/50 rounded-lg">
                  <h4 className="text-white font-medium mb-2 flex items-center">
                    <Hash className="h-4 w-4 mr-1 text-red-500" />
                    Tu código de referido
                  </h4>
                  <p className="text-lg font-mono font-bold text-red-400 bg-gray-800 p-2 rounded text-center">
                    {generateReferralCode()}
                  </p>
                  <p className="text-gray-400 text-xs mt-2">
                    Este será tu código único al registrarte. Compártelo para
                    ganar comisiones.
                  </p>
                </div>

                {/* Términos y condiciones */}
                <div className="flex items-start mt-4">
                  <div className="flex items-center h-5">
                    <input
                      id="terms"
                      type="checkbox"
                      checked={agreeTerms}
                      onChange={() => setAgreeTerms(!agreeTerms)}
                      className="h-4 w-4 text-red-500 focus:ring-red-500 border-gray-700 rounded bg-gray-800"
                      required
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="terms" className="text-gray-400">
                      Acepto los{" "}
                      <a
                        href="/terms"
                        className="text-red-500 hover:text-red-400"
                      >
                        Términos y Condiciones
                      </a>{" "}
                      y la{" "}
                      <a
                        href="/privacy"
                        className="text-red-500 hover:text-red-400"
                      >
                        Política de Privacidad
                      </a>
                    </label>
                  </div>
                </div>
              </>
            )}

            {/* Botones de navegación */}
            <div className="flex justify-between mt-6">
              {currentStep > 1 ? (
                <button
                  type="button"
                  onClick={prevStep}
                  className="bg-gray-700 text-white py-2 px-4 rounded-lg font-medium hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-all duration-300 flex items-center"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Anterior
                </button>
              ) : (
                <div></div>
              )}

              {currentStep < 5 ? (
                <button
                  type="button"
                  onClick={nextStep}
                  className="bg-gradient-to-r from-red-600 to-red-500 text-white py-2 px-4 rounded-lg font-medium shadow-lg hover:from-red-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-all duration-300 flex items-center"
                >
                  Siguiente
                  <ChevronRight className="h-4 w-4 ml-1" />
                </button>
              ) : (
                <button
                  type="submit"
                  className="bg-gradient-to-r from-red-600 to-red-500 text-white py-2 px-6 rounded-lg font-medium shadow-lg hover:from-red-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-all duration-300 transform hover:-translate-y-1"
                >
                  Completar Registro
                </button>
              )}
            </div>
          </form>

          {/* Enlaces de ayuda */}
          <div className="mt-6 text-center">
            <p className="text-gray-400">
              ¿Ya tienes una cuenta?{" "}
              <a
                href="/prediccion-zone/start"
                className="font-medium text-red-500 hover:text-red-400 transition-colors duration-200"
              >
                Iniciar sesión
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterAfiliatePage;
